import React, { useState, useContext, useEffect } from "react";
import Axios from "axios";
import AuthContext from "../../context/AuthContext";
import { API_URL } from "../../components/api";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import LanguageContext from "../../context/LanguageContext";
import "./payment.css";

function Payment({ onPaymentCreated }) {
  const [exchangeRate, setExchangeRate] = useState(0);
  const [paidUSD, setPaidUSD] = useState(0);
  const [paidLBP, setPaidLBP] = useState(0);
  const [returnedLBP, setReturnedLBP] = useState(0);
  const [description, setDescription] = useState("");
  const [selectedItem, setSelectedItem] = useState(null); // For category
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null); // Updated variable
  const [accountHolders, setAccountHolders] = useState([]);
  const [selectedAccountHolder, setSelectedAccountHolder] = useState(null);

  const [requestStatus, setRequestStatus] = useState(null); // Track status
  const [requestMessage, setRequestMessage] = useState(""); // Track message

  const [authTokens] = useState(() =>
    JSON.parse(localStorage.getItem("authTokens"))
  );
  const { user } = useContext(AuthContext);
  const { language, direction, t } = useContext(LanguageContext);

  const handleExchangeRateChange = (event) => {
    setExchangeRate(event.target.value);
  };

  const handlePaidUSDChange = (event) => {
    setPaidUSD(event.target.value);
  };

  const handlePaidLBPChange = (event) => {
    setPaidLBP(event.target.value);
  };

  const handleReturnedLBPChange = (event) => {
    setReturnedLBP(event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    let isMounted = true;

    const getFormOptions = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${authTokens.access}`,
          "Content-Type": "application/json",
        };
        const params = {
          username: user.username,
        };
        const [supplierResponse, accountHolderResponse] = await Promise.all([
          Axios.get(`${API_URL}/inventory/suppliers/`, {
            headers,
            params,
          }),
          Axios.get(`${API_URL}/inventory/warehouses/`, {
            headers,
            params: {
              ...params,
              type: "account_holders",
            },
          }),
        ]);

        if (isMounted) {
          setSuppliers(supplierResponse.data);
          setAccountHolders(accountHolderResponse.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    getFormOptions();

    return () => {
      isMounted = false;
    };
  }, [authTokens.access, user.username]);

  const clientOptions = suppliers.map((item) => ({
    label: item.fullname_en,
    value: item.supplier_id,
  }));

  const accountHolderOptions = accountHolders.map((item) => ({
    label:
      language === "ar"
        ? item.name_ar || item.name_en
        : item.name_en || item.name_ar,
    value: item.account_holder_id,
  }));

  const staticOptions = [
    { label: t("payments.expense"), value: "2" },
    { label: t("payments.loan"), value: "1" },
  ];

  const handleSubmit = async () => {
    if (!selectedAccountHolder) {
      setRequestStatus("Error");
      setRequestMessage(t("payments.accountHolderRequired"));
      return;
    }

    try {
      const authTokens = JSON.parse(localStorage.getItem("authTokens"));
      const response = await Axios.post(
        `${API_URL}/inventory/payments/`,
        {
          username: user.username,
          toDrawer: 3,
          paidLBP: paidLBP,
          paidUSD: paidUSD,
          exchangeRate: exchangeRate,
          returnedLBP: returnedLBP,
          supplier_id: selectedSupplier.value, // Access supplier_id properly
          account_holder_id: selectedAccountHolder.value,
          description: description,
          category: selectedItem.value, // Access category value properly
        },
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      if (response.status === 200) {
        // Update the balances after transfer
        const data = response.data;
        setRequestStatus(data.status); // Backend status
        setRequestMessage(data.message); // Backend message
        console.log("Response Data:", data); // Optionally log the response

        if (onPaymentCreated) {
          onPaymentCreated();
        }
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      setRequestStatus("Error");
      setRequestMessage("An error occurred while processing the payment.");
    }
  };

  return (
    <div className="collect-container payment-form" dir={direction}>
      <h5 className="collect-heading">{t("payments.formTitle")}</h5>
      <div className="collect-form payment-form-card">
        <div className="form-group row">
          <div className="col-md-12">
            <label>{t("payments.accountHolder")}:</label>
            <Select
              className="search-line"
              classNamePrefix="payment-select"
              placeholder={t("payments.chooseAccountHolder")}
              options={accountHolderOptions}
              onChange={(opt) => setSelectedAccountHolder(opt)}
              value={selectedAccountHolder}
            />
          </div>
        </div>
        <div className="form-group row">
          <div className="col-md-6">
            <label>{t("payments.supplier")}:</label>
            <Select
              className="search-line"
              classNamePrefix="payment-select"
              placeholder={t("payments.chooseSupplier")}
              options={clientOptions}
              onChange={(opt) => setSelectedSupplier(opt)} // Save the full object
              value={selectedSupplier} // Pass the full selected object
            />
          </div>
          <div className="col-md-6">
            <label>{t("payments.category")}:</label>
            <Select
              className="search-line"
              classNamePrefix="payment-select"
              placeholder={t("payments.chooseCategory")}
              options={staticOptions}
              onChange={(opt) => setSelectedItem(opt)} // Save the full object
              value={selectedItem} // Pass the full selected object
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-6">
            <label>{t("payments.exchangeRate")}:</label>
            <input
              type="number"
              value={exchangeRate}
              onChange={handleExchangeRateChange}
              className="form-control collect-input"
            />
          </div>
          <div className="col-md-6">
            <label>{t("payments.paidUsd")}:</label>
            <input
              type="number"
              value={paidUSD}
              onChange={handlePaidUSDChange}
              className="form-control collect-input"
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col-md-6">
            <label>{t("payments.paidLbp")}:</label>
            <input
              type="number"
              value={paidLBP}
              onChange={handlePaidLBPChange}
              className="form-control collect-input"
            />
          </div>
          <div className="col-md-6">
            <label>{t("payments.returnedLbp")}:</label>
            <input
              type="number"
              value={returnedLBP}
              onChange={handleReturnedLBPChange}
              className="form-control collect-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label>{t("payments.description")}:</label>
          <textarea
            value={description}
            onChange={handleDescriptionChange}
            className="form-control collect-input"
          />
        </div>

        <div className="collect-submit">
          <button
            type="button"
            className="payment-submit-button"
            onClick={handleSubmit}
          >
            {t("payments.submit")}
          </button>
        </div>
        {/* Display payment status */}
        {requestStatus && (
          <div
            className={`status-message ${
              requestStatus === "Success"
                ? "text-success"
                : requestStatus === "Pending"
                ? "text-warning"
                : "text-danger"
            }`}
          >
            {requestMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export default Payment;
